import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buyerSubmissionsApi, type RecordNdaPayload } from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  Input,
  Skeleton,
  Textarea,
  useToast,
} from "@/design-system/components/ui";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { calendarDateInputToIso, isoDateInTz, useUserTimeZone } from "@/utils/datetime";

export type NdaAttestationMode = "signed" | "sent" | "not-required";

/**
 * Recording an NDA fact (PR 3b; the third branch and the dates added by WS7).
 *
 * <p>THREE branches, one modal, because the NDA carries three INDEPENDENT facts
 * and each needs its own door. <b>Signed</b> attests that a signed NDA has been
 * filed and stamps <c>NdaSignedAtUtc</c>. <b>Sent</b> records the day it went to
 * the listing broker. <b>Not required</b> records that the broker never asked
 * for one, and why.</p>
 *
 * <p>🔴 <b>The three are not one outcome, and this modal must never resolve them
 * into one.</b> A submission that recorded "this broker does not use an NDA" and
 * then received one anyway keeps both facts — the server says so explicitly, and
 * carries every fact forward onto each new version.</p>
 *
 * <p>🔴 <b>3a's gate applies to the SIGNATURE only.</b> Recording that the NDA
 * went out, or that no NDA was required, is not Phoenix signing on somebody's
 * behalf, so neither needs a prequalification — and the server agrees, gating on
 * <c>SignedAtUtc is not null</c> alone. Wiring the other branches through one
 * gate here would invent a refusal the server does not make.</p>
 *
 * <p>🔴 <b>Every branch writes through <c>recordNda</c>, never the generic
 * version-append.</b> `RecordNdaAsync` is the only writer that runs the
 * prequalification gate, keeps the mirror columns the package gate reads, and
 * commits the new version in the same transaction. See `buyerSubmissionsApi
 * .recordNda`.</p>
 *
 * <p>🔴 <b>A picker, never an uploader.</b> The NDA reaches the system through
 * the normal Documents flow like every other document. This asks which of the
 * documents already in the submission's folder IS the NDA. An upload here would
 * be a second filing path with its own folder rules to drift from.</p>
 *
 * <p><b>Dates are typed, not assumed.</b> Both stamping branches default to
 * today and accept any past day: the paper routinely arrives before anybody gets
 * to the record, and an explicit value wins server-side precisely so a date can
 * be recorded truthfully and corrected later.</p>
 */
export function NdaAttestationModal({
  submissionId,
  open,
  onClose,
  mode,
  supersedes = false,
}: {
  submissionId: string;
  open: boolean;
  onClose: () => void;
  mode: NdaAttestationMode;
  /** True when an NDA is already on file, so the copy says what this does to it:
   *  supersede, never overwrite. */
  supersedes?: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const tz = useUserTimeZone();
  const [attested, setAttested] = useState(false);
  const [note, setNote] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [date, setDate] = useState(() => today(tz));

  const signed = mode === "signed";
  const sent = mode === "sent";

  const docs = useQuery({
    queryKey: buyerSubmissionKeys.documents(submissionId),
    queryFn: () => buyerSubmissionsApi.listDocuments(submissionId),
    enabled: open && signed,
  });

  const record = useMutation({
    mutationFn: (body: RecordNdaPayload) =>
      buyerSubmissionsApi.recordNda(submissionId, body),
    onSuccess: () => {
      // Both prefixes. The submission prefix covers the detail read, the version
      // stack this card renders, and the list's settled flag; the buyer prefix
      // covers the shortlist badge, which derives from the same fact.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success(
        signed
          ? supersedes
            ? "Replacement NDA recorded."
            : "NDA recorded."
          : sent
            ? "Recorded — NDA sent to the broker."
            : "Recorded — no NDA required.",
      );
      reset();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function reset() {
    setAttested(false);
    setNote("");
    setDocumentId("");
    setDate(today(tz));
  }

  const future = date > today(tz);
  const canSubmit = signed
    ? attested && !!date && !future
    : sent
      ? !!date && !future
      : note.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={
        signed
          ? supersedes
            ? "Record a replacement NDA"
            : "Record the signed NDA"
          : sent
            ? "Record when the NDA went to the broker"
            : "Record that no NDA was required"
      }
    >
      <div className="space-y-space-4">
        {signed && (
          <>
            <p className="text-small text-ink m-0">
              Phoenix signs the listing broker's NDA on the buyer's behalf. Confirm
              the signed copy has been filed to this submission's documents before
              recording it.
            </p>

            {supersedes && (
              // The whole point of C2: nothing is destroyed. Said plainly, so a
              // specialist recording the broker's own paper is not left
              // wondering whether they are about to lose the first one.
              <p
                className="text-small text-gray-mid m-0"
                data-testid="nda-supersede-note"
              >
                An NDA is already on file. Recording this one supersedes it — the
                earlier NDA is kept, with its dates and its notes, and stays
                readable under &ldquo;earlier NDAs&rdquo;.
              </p>
            )}

            <label className="flex items-start gap-space-2 text-small text-ink">
              <input
                type="checkbox"
                data-testid="nda-attest"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
                className="mt-[3px]"
              />
              <span>
                I confirm the signed NDA has been uploaded to the document system
                for this submission.
              </span>
            </label>
          </>
        )}

        {sent && (
          <p className="text-small text-ink m-0">
            Records the day the NDA went to the listing broker. Separate from the
            signature and from whether one was required at all — a submission can
            carry any combination of the three.
          </p>
        )}

        {(signed || sent) && (
          <div className="space-y-space-1">
            <label htmlFor="nda-date" className="block text-caption text-gray-mid">
              {signed ? "Date signed" : "Date sent to the broker"}
            </label>
            <Input
              id="nda-date"
              data-testid="nda-date"
              type="date"
              className="w-auto"
              value={date}
              max={today(tz)}
              onChange={(e) => setDate(e.target.value)}
            />
            {future && (
              <p className="text-caption text-status-warning-ink m-0">
                This records something that has already happened, so it cannot be
                a future date.
              </p>
            )}
          </div>
        )}

        {signed && (
          <div className="space-y-space-1">
            <label
              htmlFor="nda-document"
              className="block text-caption text-gray-mid"
            >
              Which document is it? (optional)
            </label>
            {docs.isLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <select
                id="nda-document"
                data-testid="nda-document"
                className="w-full rounded-sm border border-gray-mid bg-white-pure px-space-2 py-[6px] text-small text-ink"
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
              >
                <option value="">Not linked to a document</option>
                {(docs.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.title || d.fileName}
                  </option>
                ))}
              </select>
            )}
            {/* Deliberately no uploader. If the NDA is not in the list, it
                belongs in Documents first — the same route every other
                document takes. */}
            {!docs.isLoading && (docs.data ?? []).length === 0 && (
              <p className="text-caption text-gray-mid m-0">
                No documents are filed for this submission yet. Upload the NDA
                through Documents, then come back.
              </p>
            )}
          </div>
        )}

        {mode === "not-required" && (
          <p className="text-small text-ink m-0">
            Use this when the listing broker never asked for an NDA. It records
            the reason so a blank signature date is not mistaken for something
            still outstanding.
          </p>
        )}

        {/* 🔴 The note is an ENTRY on the new version, not a column any later
            save can rewrite — so it is dated and attributed to whoever writes
            it here, and it stays that way. */}
        <div className="space-y-space-1">
          <label htmlFor="nda-note" className="block text-caption text-gray-mid">
            {mode === "not-required" ? "Why was none required?" : "Note (optional)"}
          </label>
          <Textarea
            id="nda-note"
            data-testid="nda-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              signed
                ? "Anything worth recording about the authorisation"
                : sent
                  ? "How it went out, and to whom"
                  : "e.g. the broker publishes financials without one"
            }
          />
        </div>

        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={() => { reset(); onClose(); }}>
            Cancel
          </Button>
          <Button
            data-testid="nda-submit"
            disabled={!canSubmit || record.isPending}
            onClick={() =>
              record.mutate(
                signed
                  ? {
                      signedAtUtc: asInstant(date),
                      ndaDocumentId: documentId || null,
                      authorizationNote: note.trim() || null,
                    }
                  : sent
                    ? {
                        sentToBrokerAtUtc: asInstant(date),
                        authorizationNote: note.trim() || null,
                      }
                    : { notRequiredReason: note.trim() })
            }
          >
            {record.isPending ? "Recording…" : "Record"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Today as `YYYY-MM-DD` in the VIEWER'S zone, which is what a `type="date"`
 * input wants.
 *
 * <p>Read through `isoDateInTz` rather than shifting an instant by the
 * BROWSER's offset: that spelling produces a third "today" agreeing with
 * neither UTC nor the app's zone, and it disagrees with every other date this
 * product renders. The seed, the `max=` bound and the submit gate all call
 * this one function, so they cannot drift apart.</p>
 */
function today(tz: string): string {
  return isoDateInTz(new Date(), tz);
}

/**
 * A picked calendar day as the instant the wire expects.
 *
 * Noon UTC, not midnight: a wall-calendar day stored at `T00:00:00Z` renders as
 * the PREVIOUS day through any tz-aware formatter west of Greenwich. Noon
 * survives being re-rendered in every zone from UTC-11 to UTC+11 as the day
 * that was actually typed.
 */
function asInstant(day: string): string {
  return calendarDateInputToIso(day)!;
}
